// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Estoque/VeiculosEstoque.tsx
//  Estoque de veículos da revenda (RENAVE) — lista + cadastro de compra.
//  Fatia 4: receber de filial, transferências recebidas e painel de pendências.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowDownToLine, Car, ClipboardList, Loader2, Plus, Search, Settings, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getApiErrorMessage } from '@/lib/apiClient';
import { toast } from '@/components/ui/sonner';
import {
  renaveApi, ORIGEM, SITUACAO, SITUACAO_LABEL, descricaoVeiculo, formatarMoeda, type AutorizacaoRecebida,
} from '@/features/renave/api';
import { AutorizacoesRecebidasDialog, ReceberFilialDialog } from '@/features/renave/ReceberTransferencia';
import { VeiculoEstoqueForm, estadoVazio, paraInput, type VeiculoFormState } from '@/features/renave/VeiculoEstoqueForm';

const TODAS = '__todas__';

export function SituacaoBadge({ situacao }: { situacao: number }) {
  const variante = situacao === SITUACAO.EmEstoque ? 'default'
    : situacao === SITUACAO.Cancelado ? 'destructive'
    : situacao === SITUACAO.Vendido || situacao === SITUACAO.Transferido ? 'secondary' : 'outline';
  return <Badge variant={variante}>{SITUACAO_LABEL[situacao] ?? situacao}</Badge>;
}

export default function VeiculosEstoque() {
  const navigate = useNavigate();
  const [situacao, setSituacao] = useState<string>(TODAS);
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [novoAberto, setNovoAberto] = useState(false);
  const [form, setForm] = useState<VeiculoFormState>(estadoVazio);
  const [salvando, setSalvando] = useState(false);
  const [filialAberto, setFilialAberto] = useState(false);
  const [recebidasAberto, setRecebidasAberto] = useState(false);

  const cadastrarRecebimento = (a: AutorizacaoRecebida) => {
    setRecebidasAberto(false);
    setForm({
      ...estadoVazio(), origemEntrada: ORIGEM.TransferenciaEstabelecimento,
      chassi: a.chassi ?? '', placa: a.placa ?? '', cnpjOrigemTransferencia: a.cnpjOrigem ?? '',
    });
    setNovoAberto(true);
  };

  const { data: veiculos, isLoading, error } = useQuery({
    queryKey: ['veiculos-estoque', situacao, buscaAplicada],
    queryFn: () => renaveApi.listar({
      situacao: situacao === TODAS ? undefined : Number(situacao),
      busca: buscaAplicada || undefined,
    }),
  });

  const { data: config } = useQuery({ queryKey: ['renave-config'], queryFn: renaveApi.config });

  const criar = async () => {
    if (!form.chassi.trim()) { toast.error('Informe o chassi.'); return; }
    setSalvando(true);
    try {
      const v = await renaveApi.criar(paraInput(form));
      toast.success('Veículo cadastrado.');
      setNovoAberto(false);
      navigate(`/estoque/veiculos/${v.id}`);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Car className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Estoque de veículos</h1>
            <p className="text-sm text-muted-foreground">
              Compras e vendas registradas no RENAVE
              {config && !config.ativo && ' · RENAVE desativado para esta empresa'}
              {config?.ativo && config.ambiente === 2 && ' · ambiente de homologação'}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate('/configuracoes/renave')}>
            <Settings className="mr-2 h-4 w-4" />Configuração
          </Button>
          <Button variant="outline" onClick={() => navigate('/estoque/veiculos/pendencias')}>
            <ClipboardList className="mr-2 h-4 w-4" />Pendências
          </Button>
          <Button variant="outline" onClick={() => setFilialAberto(true)} disabled={!config?.ativo}>
            <Truck className="mr-2 h-4 w-4" />Receber de filial
          </Button>
          <Button variant="outline" onClick={() => setRecebidasAberto(true)} disabled={!config?.ativo}>
            <ArrowDownToLine className="mr-2 h-4 w-4" />Transferências recebidas
          </Button>
          <Button onClick={() => { setForm(estadoVazio()); setNovoAberto(true); }}>
            <Plus className="mr-2 h-4 w-4" />Nova compra
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Select value={situacao} onValueChange={setSituacao}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={TODAS}>Todas as situações</SelectItem>
            {Object.entries(SITUACAO_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <form className="flex gap-2" onSubmit={e => { e.preventDefault(); setBuscaAplicada(busca.trim()); }}>
          <Input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Placa, chassi, marca ou modelo" className="w-72" />
          <Button type="submit" variant="secondary"><Search className="h-4 w-4" /></Button>
        </form>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Veículo</TableHead>
              <TableHead>Placa</TableHead>
              <TableHead>Chassi</TableHead>
              <TableHead>Compra</TableHead>
              <TableHead>Situação</TableHead>
              <TableHead>RENAVE</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></TableCell></TableRow>
            ) : error ? (
              <TableRow><TableCell colSpan={6} className="text-center text-destructive">{getApiErrorMessage(error)}</TableCell></TableRow>
            ) : !veiculos?.length ? (
              <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">Nenhum veículo encontrado.</TableCell></TableRow>
            ) : veiculos.map(v => (
              <TableRow key={v.id} className="cursor-pointer" onClick={() => navigate(`/estoque/veiculos/${v.id}`)}>
                <TableCell className="font-medium">{descricaoVeiculo(v)}</TableCell>
                <TableCell className="font-mono">{v.placa ?? '—'}</TableCell>
                <TableCell className="font-mono text-xs">{v.chassi}</TableCell>
                <TableCell>
                  <div>{formatarMoeda(v.valorCompra)}</div>
                  <div className="text-xs text-muted-foreground">
                    {v.dataCompra ? new Date(v.dataCompra.slice(0, 10) + 'T00:00:00').toLocaleDateString('pt-BR') : ''}
                  </div>
                </TableCell>
                <TableCell><SituacaoBadge situacao={v.situacao} /></TableCell>
                <TableCell className="text-xs">{v.renaveEstado ?? (v.situacao === SITUACAO.EmCompra ? 'Sem entrada' : '—')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={novoAberto} onOpenChange={setNovoAberto}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form.origemEntrada === ORIGEM.Compra ? 'Nova compra de veículo' : 'Receber veículo de outra revenda'}</DialogTitle>
            <DialogDescription>
              Cadastra o veículo e cria o produto dele. {form.origemEntrada === ORIGEM.Compra
                ? 'A entrada no RENAVE é feita na tela seguinte.'
                : 'O pedido de transferência ao RENAVE é feito na tela seguinte.'}
            </DialogDescription>
          </DialogHeader>
          <VeiculoEstoqueForm valor={form} onChange={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNovoAberto(false)}>Cancelar</Button>
            <Button onClick={criar} disabled={salvando}>
              {salvando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Cadastrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReceberFilialDialog aberto={filialAberto} onClose={() => setFilialAberto(false)} />
      <AutorizacoesRecebidasDialog aberto={recebidasAberto} onClose={() => setRecebidasAberto(false)} onCadastrar={cadastrarRecebimento} />
    </div>
  );
}
